import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { authService } from '../../services/authService';

// ── Constantes ────────────────────────────────────────────────────────────────

const FASES_DEF = [
  { id: 'presupuesto',  label: 'Presupuesto',     emoji: '📋', desc: 'Presupuesto aprobado por el cliente' },
  { id: 'material',     label: 'Material',         emoji: '📦', desc: 'Material pedido y recibido' },
  { id: 'instalacion',  label: 'Instalación',      emoji: '🔧', desc: 'Trabajo de instalación en curso' },
  { id: 'pruebas',      label: 'Pruebas',          emoji: '✅', desc: 'Pruebas de funcionamiento OK' },
  { id: 'finalizacion', label: 'Finalización',     emoji: '🏁', desc: 'Trabajos acabados y revisados' },
  { id: 'entrega',      label: 'Entrega al cliente',emoji: '🤝', desc: 'Acta de entrega firmada' },
];

const ESTADOS_INST = [
  { id: 'en_curso',    label: 'En curso',    color: 'bg-blue-100 text-blue-700' },
  { id: 'pausada',     label: 'Pausada',     color: 'bg-yellow-100 text-yellow-700' },
  { id: 'completada',  label: 'Completada',  color: 'bg-green-100 text-green-700' },
  { id: 'cancelada',   label: 'Cancelada',   color: 'bg-red-100 text-red-700' },
];

const TIPOS_INST = [
  'Aerotermia', 'Aire acondicionado', 'Calefacción', 'Suelo radiante',
  'Solar térmica', 'Bomba de calor', 'Fontanería', 'Otro',
];

function estadoInfo(estado) {
  return ESTADOS_INST.find(e => e.id === estado) || { label: estado, color: 'bg-gray-100 text-gray-500' };
}

function fasesProgress(fases = {}) {
  const total = FASES_DEF.length;
  const done = FASES_DEF.filter(f => fases[f.id]?.completada).length;
  return { done, total, pct: Math.round((done / total) * 100) };
}

// ── Utilidades ────────────────────────────────────────────────────────────────

function Spinner() {
  return <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto" />;
}

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-ES'); } catch { return d; }
};

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';
const labelCls = 'block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1';

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

// ── Formulario de nueva instalación ──────────────────────────────────────────

const EMPTY_INST = {
  clienteNombre: '', clienteTelefono: '', clienteEmail: '', clienteDireccion: '',
  tipo: '', descripcion: '', tecnicoAsignado: '',
  fechaInicio: '', fechaEstimadaFin: '',
  presupuestoRef: '', presupuestoId: '', ordenRef: '',
  estado: 'en_curso', notas: '', lineas: [], importeTotal: null,
};

function InstalacionForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_INST);
  const [presupuestos, setPresupuestos] = useState([]);
  const [loadingPres, setLoadingPres] = useState(false);
  const [showPresModal, setShowPresModal] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const abrirImportarPres = async () => {
    setLoadingPres(true);
    setShowPresModal(true);
    try {
      const r = await fetch('/.netlify/functions/getPresupuestos', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      setPresupuestos((d.presupuestos || []).filter(p => p.estado === 'aceptado'));
    } catch { setPresupuestos([]); }
    finally { setLoadingPres(false); }
  };

  const importarPresupuesto = (pres) => {
    setForm(f => ({
      ...f,
      clienteNombre:    pres.clienteNombre    || f.clienteNombre,
      clienteEmail:     pres.clienteEmail     || f.clienteEmail,
      clienteTelefono:  pres.clienteTelefono  || f.clienteTelefono,
      clienteDireccion: pres.clienteDireccion || f.clienteDireccion,
      descripcion:      [pres.titulo, pres.descripcion].filter(Boolean).join('\n') || f.descripcion,
      lineas:           pres.lineas || [],
      importeTotal:     pres.total ?? f.importeTotal,
      presupuestoRef:   pres.numero || f.presupuestoRef,
      presupuestoId:    pres.id    || f.presupuestoId,
    }));
    setShowPresModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Importar desde presupuesto */}
      <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <span className="text-blue-600 text-lg">📋</span>
        <div className="flex-1 text-sm text-blue-700">
          {form.presupuestoRef
            ? <span>Datos importados de <strong>{form.presupuestoRef}</strong></span>
            : <span>¿Tienes un presupuesto aprobado? Importa los datos automáticamente.</span>
          }
        </div>
        <button
          onClick={abrirImportarPres}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
        >
          {form.presupuestoRef ? 'Cambiar' : 'Importar presupuesto'}
        </button>
      </div>

      {/* Modal selector de presupuesto */}
      {showPresModal && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowPresModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h4 className="font-bold text-gray-800">Seleccionar presupuesto aprobado</h4>
              <button onClick={() => setShowPresModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="p-4">
              {loadingPres ? (
                <div className="text-center py-6 text-gray-400 text-sm">Cargando…</div>
              ) : presupuestos.length === 0 ? (
                <div className="text-center py-6 text-gray-400 text-sm">No hay presupuestos aprobados disponibles.</div>
              ) : (
                <div className="space-y-2">
                  {presupuestos.map(p => (
                    <button
                      key={p.id}
                      onClick={() => importarPresupuesto(p)}
                      className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-mono text-xs text-gray-500">{p.numero}</span>
                        <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Aprobado</span>
                      </div>
                      <div className="font-semibold text-gray-800 text-sm">{p.clienteNombre}</div>
                      <div className="text-xs text-gray-500 truncate">{p.titulo}</div>
                      {p.total != null && (
                        <div className="text-xs font-bold text-gray-700 mt-0.5">
                          {Number(p.total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Cliente *">
          <input className={inputCls} value={form.clienteNombre} onChange={e => set('clienteNombre', e.target.value)} placeholder="Nombre o empresa" />
        </Field>
        <Field label="Teléfono">
          <input className={inputCls} value={form.clienteTelefono} onChange={e => set('clienteTelefono', e.target.value)} placeholder="600 000 000" />
        </Field>
        <Field label="Email">
          <input className={inputCls} type="email" value={form.clienteEmail} onChange={e => set('clienteEmail', e.target.value)} placeholder="cliente@email.com" />
        </Field>
        <Field label="Dirección de la instalación">
          <input className={inputCls} value={form.clienteDireccion} onChange={e => set('clienteDireccion', e.target.value)} placeholder="Dirección completa" />
        </Field>
        <Field label="Tipo de instalación">
          <select className={inputCls} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
            <option value="">— Seleccionar —</option>
            {TIPOS_INST.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Técnico asignado">
          <select className={inputCls} value={form.tecnicoAsignado} onChange={e => set('tecnicoAsignado', e.target.value)}>
            <option value="">— Sin asignar —</option>
            {['Benito', 'Pablo', 'Dani', 'Ivan'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Fecha de inicio">
          <input type="date" className={inputCls} value={form.fechaInicio} onChange={e => set('fechaInicio', e.target.value)} />
        </Field>
        <Field label="Fecha estimada de fin">
          <input type="date" className={inputCls} value={form.fechaEstimadaFin} onChange={e => set('fechaEstimadaFin', e.target.value)} />
        </Field>
        <Field label="Referencia presupuesto">
          <input className={inputCls} value={form.presupuestoRef} onChange={e => set('presupuestoRef', e.target.value)} placeholder="PRES-00001" />
        </Field>
        <Field label="Referencia orden de trabajo">
          <input className={inputCls} value={form.ordenRef} onChange={e => set('ordenRef', e.target.value)} placeholder="ZAP-00001 o similar" />
        </Field>
      </div>
      <Field label="Descripción del trabajo">
        <textarea className={inputCls} rows={3} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Descripción general del proyecto de instalación…" />
      </Field>
      {form.importeTotal != null && (
        <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2 text-sm">
          <span className="text-gray-500">Importe del presupuesto:</span>
          <span className="font-bold text-gray-800">
            {Number(form.importeTotal).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
          </span>
        </div>
      )}
      <Field label="Notas internas">
        <textarea className={inputCls} rows={2} value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Observaciones, incidencias, accesos…" />
      </Field>
      <div className="flex gap-3">
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.clienteNombre.trim()}
          className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? 'Guardando…' : 'Crear instalación'}
        </button>
        <button onClick={onCancel} className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Panel de detalle con fases ────────────────────────────────────────────────

function InstalacionDetalle({ inst, onClose, onUpdate }) {
  const [fases, setFases] = useState(inst.fases || {});
  const [saving, setSaving] = useState(false);
  const [editFase, setEditFase] = useState(null);
  const [faseNotas, setFaseNotas] = useState('');
  const [faseDate, setFaseDate] = useState('');
  const [creandoFactura, setCreandoFactura] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [docNombre, setDocNombre] = useState('');
  const [docUrl, setDocUrl] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);

  const { done, total, pct } = fasesProgress(fases);
  const ei = estadoInfo(inst.estado);

  const openFase = (faseId) => {
    const f = fases[faseId] || {};
    setFaseNotas(f.notas || '');
    setFaseDate(f.fecha || '');
    setEditFase(faseId);
  };

  const saveFase = async (faseId, completada) => {
    const updatedFases = {
      ...fases,
      [faseId]: { completada, fecha: faseDate || (completada ? new Date().toISOString().split('T')[0] : null), notas: faseNotas },
    };

    const allDone = FASES_DEF.every(f => updatedFases[f.id]?.completada);
    const anyDone = FASES_DEF.some(f => updatedFases[f.id]?.completada);
    const estado = allDone ? 'completada' : (anyDone ? 'en_curso' : inst.estado);

    // Email al cliente en hitos clave (solo al completar, no al desmarcar)
    const emailMilestone = (completada && ['instalacion', 'entrega'].includes(faseId) && inst.clienteEmail)
      ? {
          fase: faseId,
          clienteEmail: inst.clienteEmail,
          clienteNombre: inst.clienteNombre,
          tipo: inst.tipo,
          direccion: inst.clienteDireccion,
          tecnicoNombre: inst.tecnicoAsignado,
          fechaInicio: inst.fechaInicio,
        }
      : undefined;

    setSaving(true);
    try {
      const resp = await fetch('/.netlify/functions/saveInstalacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id: inst.id, fases: updatedFases, estado, emailMilestone }),
      });
      const d = await resp.json();
      if (d.success) {
        setFases(updatedFases);
        onUpdate({ ...inst, fases: updatedFases, estado });
        setEditFase(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCertificadoPDF = () => {
    const hoy  = new Date().toLocaleDateString('es-ES');
    const fmtF = iso => iso ? iso.split('-').reverse().join('/') : '—';

    const lineasHtml = (inst.lineas || []).map(l =>
      `<tr><td style="padding:5px 8px">${l.descripcion || ''}</td><td style="padding:5px 8px;text-align:right">${Number(l.cantidad || 1).toFixed(0)}</td><td style="padding:5px 8px;text-align:right">${Number(l.precio || 0).toFixed(2).replace('.', ',')} €</td></tr>`
    ).join('');

    const fasesHtml = FASES_DEF.map(f => {
      const fase = fases[f.id] || {};
      return `<tr>
        <td style="padding:6px 8px">${f.emoji} ${f.label}</td>
        <td style="padding:6px 8px;text-align:center;color:${fase.completada ? '#16a34a' : '#9ca3af'}">${fase.completada ? '✓' : '—'}</td>
        <td style="padding:6px 8px;text-align:center">${fase.completada ? fmtF(fase.fecha) : '—'}</td>
        <td style="padding:6px 8px;color:#6b7280;font-size:12px">${fase.notas || ''}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Certificado instalación ${inst.numero || ''}</title>
<style>
  body{font-family:Arial,sans-serif;font-size:13px;color:#111;max-width:800px;margin:32px auto;padding:0 24px}
  .hdr{background:#DC2626;color:#fff;padding:18px 24px;border-radius:8px;margin-bottom:20px}
  .hdr h1{margin:0;font-size:20px}.hdr p{margin:4px 0 0;font-size:12px;opacity:.85}
  .ct{text-align:center;font-size:16px;font-weight:bold;color:#DC2626;text-transform:uppercase;letter-spacing:1px;margin:20px 0}
  .stitle{font-size:10px;font-weight:bold;text-transform:uppercase;letter-spacing:.5px;color:#DC2626;border-bottom:1.5px solid #DC2626;padding-bottom:3px;margin:16px 0 8px}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px}
  .field label{font-size:10px;color:#6b7280}.field span{font-weight:bold;display:block;margin-top:1px}
  table{width:100%;border-collapse:collapse}
  table th{background:#f9fafb;font-size:11px;text-align:left;padding:6px 8px;border-bottom:2px solid #e5e7eb}
  table td{border-bottom:1px solid #f3f4f6}
  .firma-area{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:36px}
  .firma-box{border:1px solid #d1d5db;padding:12px;border-radius:6px;min-height:70px}
  .flabel{font-size:10px;color:#6b7280;margin-bottom:4px}
  .footer{margin-top:32px;border-top:1px solid #e5e7eb;padding-top:12px;color:#9ca3af;font-size:11px}
  @media print{body{margin:16px}}
</style></head><body>
<div class="hdr"><h1>Climatización Zapata</h1><p>SAT oficial Ariston · Servicios de climatización y aerotermia</p></div>
<div class="ct">Certificado de instalación completada</div>
<div class="stitle">Datos del proyecto</div>
<div class="grid">
  <div class="field"><label>Referencia</label><span>${inst.numero || '—'}</span></div>
  <div class="field"><label>Fecha certificado</label><span>${hoy}</span></div>
  ${inst.presupuestoRef ? `<div class="field"><label>Presupuesto</label><span>${inst.presupuestoRef}</span></div>` : ''}
  ${inst.tecnicoAsignado ? `<div class="field"><label>Técnico responsable</label><span>${inst.tecnicoAsignado}</span></div>` : ''}
  ${inst.fechaInicio ? `<div class="field"><label>Inicio</label><span>${fmtF(inst.fechaInicio)}</span></div>` : ''}
  ${fases.entrega?.fecha ? `<div class="field"><label>Fecha de entrega</label><span>${fmtF(fases.entrega.fecha)}</span></div>` : ''}
</div>
<div class="stitle">Cliente</div>
<div class="grid">
  <div class="field"><label>Nombre</label><span>${inst.clienteNombre || '—'}</span></div>
  ${inst.clienteTelefono ? `<div class="field"><label>Teléfono</label><span>${inst.clienteTelefono}</span></div>` : ''}
  ${inst.clienteEmail ? `<div class="field"><label>Email</label><span>${inst.clienteEmail}</span></div>` : ''}
  ${inst.clienteDireccion ? `<div class="field" style="grid-column:span 2"><label>Dirección</label><span>${inst.clienteDireccion}</span></div>` : ''}
</div>
${inst.descripcion ? `<div class="stitle">Descripción de los trabajos</div><p style="margin:0;line-height:1.6">${inst.descripcion.replace(/\n/g,'<br>')}</p>` : ''}
${lineasHtml ? `<div class="stitle">Partidas ejecutadas</div>
<table><thead><tr><th>Descripción</th><th style="text-align:right">Uds.</th><th style="text-align:right">Precio</th></tr></thead>
<tbody>${lineasHtml}</tbody></table>
${inst.importeTotal != null ? `<div style="text-align:right;margin-top:6px;font-size:14px"><strong>Total: ${Number(inst.importeTotal).toLocaleString('es-ES',{style:'currency',currency:'EUR'})}</strong></div>` : ''}` : ''}
<div class="stitle">Seguimiento de fases</div>
<table><thead><tr><th>Fase</th><th style="text-align:center">Estado</th><th style="text-align:center">Fecha</th><th>Notas</th></tr></thead>
<tbody>${fasesHtml}</tbody></table>
<div class="firma-area">
  <div class="firma-box"><div class="flabel">Firma del cliente</div><div style="margin-top:6px;font-size:12px;color:#374151">${inst.clienteNombre || ''}</div></div>
  <div class="firma-box"><div class="flabel">Por Climatización Zapata</div><div style="margin-top:6px;font-size:12px;color:#374151">${inst.tecnicoAsignado || ''}</div></div>
</div>
<div class="footer"><p>📞 966 766 306 · ✉️ info@climatizacionzapata.es · Climatización Zapata</p><p>Documento generado el ${hoy}</p></div>
<script>setTimeout(()=>window.print(),300)</script>
</body></html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  };

  const crearFactura = async () => {
    if (!window.confirm(
      `¿Crear factura para la instalación ${inst.numero} — ${inst.clienteNombre}?\n\n` +
      `Importe: ${inst.importeTotal != null ? Number(inst.importeTotal).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : 'No especificado'}\n\n` +
      `Esta acción creará una factura en el sistema.`
    )) return;
    setCreandoFactura(true);
    try {
      const resp = await fetch('/.netlify/functions/saveFactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteNombre: inst.clienteNombre,
          clienteEmail:  inst.clienteEmail  || null,
          importe:       inst.importeTotal ? Number(inst.importeTotal) / 1.21 : null,
          importeTotal:  inst.importeTotal  ? Number(inst.importeTotal) : null,
          tipoIva:       21,
          notas:         `Instalación ${inst.numero} — ${inst.tipo || ''}`,
          lineas:        inst.lineas || [],
          origen:        'instalacion',
          instalacionId: inst.id,
          instalacionRef: inst.numero,
        }),
      });
      const d = await resp.json();
      if (d.success) {
        alert(`Factura creada: ${d.referencia}`);
        // Marcar instalación como facturada
        await fetch('/.netlify/functions/saveInstalacion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ id: inst.id, facturada: true, facturaRef: d.referencia }),
        });
        onUpdate({ ...inst, fases, facturada: true, facturaRef: d.referencia });
      } else {
        alert('Error: ' + (d.error || 'No se pudo crear la factura'));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setCreandoFactura(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-mono text-sm font-bold text-gray-500">{inst.numero}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ei.color}`}>{ei.label}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">{inst.clienteNombre}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{inst.tipo} · {inst.clienteDireccion || '—'}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4">×</button>
          </div>

          {/* Barra de progreso */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Progreso de fases</span>
              <span className="font-bold">{done}/{total} — {pct}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Banner: instalación completada — crear factura */}
          {pct === 100 && !inst.facturada && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-green-600 text-xl">✅</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-green-800">Todas las fases completadas</p>
                <p className="text-xs text-green-600">La instalación está lista para facturar.</p>
              </div>
              <button
                onClick={crearFactura}
                disabled={creandoFactura}
                className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {creandoFactura ? 'Creando…' : 'Crear factura'}
              </button>
            </div>
          )}
          {inst.facturada && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 flex items-center gap-2">
              <span className="text-green-600">✅</span>
              <span className="text-sm text-gray-700 font-medium">Factura emitida{inst.facturaRef ? `: ${inst.facturaRef}` : ''}</span>
            </div>
          )}
          {pct === 100 && (
            <div className="flex justify-end">
              <button
                onClick={handleCertificadoPDF}
                className="px-3 py-1.5 border border-gray-200 bg-white rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium flex items-center gap-1.5"
              >
                📋 Certificado PDF
              </button>
            </div>
          )}

          {/* Datos */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div><span className="text-gray-400">Técnico: </span><span className="font-semibold">{inst.tecnicoAsignado || '—'}</span></div>
            <div><span className="text-gray-400">Inicio: </span><span className="font-semibold">{fmtDate(inst.fechaInicio)}</span></div>
            <div><span className="text-gray-400">Fin estimado: </span><span className="font-semibold">{fmtDate(inst.fechaEstimadaFin)}</span></div>
            {inst.presupuestoRef && <div><span className="text-gray-400">Presupuesto: </span><span className="font-semibold">{inst.presupuestoRef}</span></div>}
            {inst.ordenRef && <div><span className="text-gray-400">Orden: </span><span className="font-semibold">{inst.ordenRef}</span></div>}
            {inst.importeTotal != null && <div><span className="text-gray-400">Importe: </span><span className="font-semibold">{Number(inst.importeTotal).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>}
          </div>

          {inst.descripcion && (
            <div>
              <p className={labelCls}>Descripción</p>
              <p className="text-sm text-gray-700">{inst.descripcion}</p>
            </div>
          )}

          {/* Fases */}
          <div>
            <p className={labelCls}>Seguimiento de fases</p>
            <div className="space-y-2 mt-2">
              {FASES_DEF.map((faseDef, idx) => {
                const fase = fases[faseDef.id] || {};
                const isEditando = editFase === faseDef.id;
                const prevDone = idx === 0 || fases[FASES_DEF[idx - 1].id]?.completada;

                return (
                  <div
                    key={faseDef.id}
                    className={`rounded-xl border p-4 ${
                      fase.completada
                        ? 'border-green-200 bg-green-50'
                        : !prevDone
                        ? 'border-gray-100 bg-gray-50 opacity-60'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{faseDef.emoji}</span>
                        <div>
                          <div className="font-semibold text-sm text-gray-800">
                            {idx + 1}. {faseDef.label}
                          </div>
                          <div className="text-xs text-gray-400">{faseDef.desc}</div>
                          {fase.completada && fase.fecha && (
                            <div className="text-xs text-green-600 font-semibold mt-0.5">
                              Completada el {fmtDate(fase.fecha)}
                            </div>
                          )}
                          {fase.notas && !isEditando && (
                            <div className="text-xs text-gray-500 mt-0.5 italic">{fase.notas}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {fase.completada ? (
                          <>
                            <span className="text-green-600 font-bold text-sm">✓</span>
                            <button
                              onClick={() => openFase(faseDef.id)}
                              className="text-xs text-gray-400 hover:text-gray-600"
                            >
                              Editar
                            </button>
                          </>
                        ) : prevDone ? (
                          <button
                            onClick={() => openFase(faseDef.id)}
                            className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700"
                          >
                            Completar
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Bloqueada</span>
                        )}
                      </div>
                    </div>

                    {/* Editor de fase inline */}
                    {isEditando && (
                      <div className="mt-4 space-y-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={labelCls}>Fecha de completación</label>
                            <input
                              type="date"
                              className={inputCls}
                              value={faseDate}
                              onChange={e => setFaseDate(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={labelCls}>Notas</label>
                            <input
                              className={inputCls}
                              value={faseNotas}
                              onChange={e => setFaseNotas(e.target.value)}
                              placeholder="Observaciones opcionales…"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveFase(faseDef.id, true)}
                            disabled={saving}
                            className="px-4 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {saving ? '…' : '✓ Marcar completada'}
                          </button>
                          {fase.completada && (
                            <button
                              onClick={() => saveFase(faseDef.id, false)}
                              disabled={saving}
                              className="px-4 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 disabled:opacity-50"
                            >
                              Desmarcar
                            </button>
                          )}
                          <button
                            onClick={() => setEditFase(null)}
                            className="px-4 py-1.5 border border-gray-200 text-xs font-semibold rounded-lg hover:bg-gray-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notas internas */}
          {inst.notas && (
            <div>
              <p className={labelCls}>Notas internas</p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{inst.notas}</p>
            </div>
          )}

          {/* Documentos adjuntos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={labelCls}>Documentos y fotos</p>
              <button onClick={() => setShowDocForm(v => !v)}
                className="text-xs text-red-600 font-semibold hover:text-red-700">
                + Añadir enlace
              </button>
            </div>

            {showDocForm && (
              <div className="bg-gray-50 rounded-xl p-3 space-y-2 mb-3">
                <input
                  className={inputCls}
                  placeholder="Nombre del documento (ej: Foto antes instalación)"
                  value={docNombre}
                  onChange={e => setDocNombre(e.target.value)}
                />
                <input
                  className={inputCls}
                  placeholder="URL del documento (Google Drive, OneDrive, foto...)"
                  value={docUrl}
                  onChange={e => setDocUrl(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    disabled={savingDoc || !docNombre.trim() || !docUrl.trim()}
                    onClick={async () => {
                      if (!docNombre.trim() || !docUrl.trim()) return;
                      setSavingDoc(true);
                      const nuevos = [...(inst.documentos || []), {
                        id: Date.now().toString(),
                        nombre: docNombre.trim(),
                        url: docUrl.trim(),
                        fecha: new Date().toISOString().split('T')[0],
                      }];
                      try {
                        const r = await fetch('/.netlify/functions/saveInstalacion', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
                          body: JSON.stringify({ id: inst.id, documentos: nuevos }),
                        });
                        const d = await r.json();
                        if (d.success) {
                          onUpdate({ ...inst, documentos: nuevos });
                          setDocNombre(''); setDocUrl(''); setShowDocForm(false);
                        }
                      } finally { setSavingDoc(false); }
                    }}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {savingDoc ? 'Guardando…' : 'Guardar'}
                  </button>
                  <button onClick={() => { setShowDocForm(false); setDocNombre(''); setDocUrl(''); }}
                    className="px-3 py-1.5 border border-gray-200 text-xs rounded-lg hover:bg-gray-100">
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {(inst.documentos || []).length === 0 && !showDocForm && (
              <p className="text-xs text-gray-400 italic">Sin documentos adjuntos</p>
            )}
            <div className="space-y-1.5">
              {(inst.documentos || []).map(doc => (
                <div key={doc.id || doc.nombre} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-sm">📎</span>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:underline flex-1 truncate">
                    {doc.nombre}
                  </a>
                  <span className="text-xs text-gray-400 flex-shrink-0">{doc.fecha}</span>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`¿Eliminar "${doc.nombre}"?`)) return;
                      const nuevos = (inst.documentos || []).filter(d => d.id !== doc.id && d.nombre !== doc.nombre);
                      const r = await fetch('/.netlify/functions/saveInstalacion', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
                        body: JSON.stringify({ id: inst.id, documentos: nuevos }),
                      });
                      const d = await r.json();
                      if (d.success) onUpdate({ ...inst, documentos: nuevos });
                    }}
                    className="text-gray-300 hover:text-red-500 text-xs flex-shrink-0">✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Proponer contrato de mantenimiento */}
          {fases.entrega?.completada && !inst.contratoMantenimientoId && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-xl">🔄</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-blue-800">Instalación entregada</p>
                <p className="text-xs text-blue-600">¿Deseas ofrecer un contrato de mantenimiento al cliente?</p>
              </div>
              <button
                onClick={() => {
                  const params = new URLSearchParams({
                    clienteNombre: inst.clienteNombre || '',
                    clienteEmail:  inst.clienteEmail  || '',
                    clienteTel:    inst.clienteTelefono || '',
                    dir:           inst.clienteDireccion || '',
                    tipo:          inst.tipo || '',
                    instalacionId: inst.id,
                    instalacionRef: inst.numero || '',
                  });
                  window.open(`/mantenimientos?nuevo=1&${params}`, '_self');
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 flex-shrink-0"
              >
                Crear contrato
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tarjeta kanban de instalación ─────────────────────────────────────────────

function InstCard({ inst, onClick }) {
  const { done, total, pct } = fasesProgress(inst.fases || {});
  const ei = estadoInfo(inst.estado);
  const faseActual = FASES_DEF.find(f => !inst.fases?.[f.id]?.completada);
  const hoy = new Date().toISOString().split('T')[0];
  const retrasada = inst.fechaEstimadaFin && inst.estado !== 'completada' && inst.estado !== 'cancelada' && inst.fechaEstimadaFin < hoy;

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border p-4 hover:shadow-md cursor-pointer transition-all space-y-3 ${retrasada ? 'border-red-300 hover:border-red-400' : 'border-gray-200 hover:border-red-200'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] text-gray-400">{inst.numero}</div>
          <div className="font-semibold text-gray-800 text-sm mt-0.5 truncate">{inst.clienteNombre}</div>
          <div className="text-xs text-gray-400 truncate">{inst.tipo} · {inst.clienteDireccion || '—'}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ei.color}`}>{ei.label}</span>
          {retrasada && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              ⚠ Retrasada
            </span>
          )}
        </div>
      </div>

      {/* Progreso */}
      <div>
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>{faseActual ? `Fase: ${faseActual.label}` : '✅ Completada'}</span>
          <span>{done}/{total}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Emojis de fases */}
      <div className="flex gap-1">
        {FASES_DEF.map(f => (
          <span
            key={f.id}
            title={f.label}
            className={`text-base ${inst.fases?.[f.id]?.completada ? '' : 'grayscale opacity-30'}`}
          >
            {f.emoji}
          </span>
        ))}
      </div>

      {inst.tecnicoAsignado && (
        <div className="text-[10px] text-gray-400">👤 {inst.tecnicoAsignado}</div>
      )}
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

export default function InstalacionesView() {
  const [instalaciones, setInstalaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'lista'

  const cargar = useCallback(() => {
    setLoading(true);
    fetch('/.netlify/functions/getInstalaciones', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { setInstalaciones(d.success ? d.instalaciones : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    if (!search.trim()) return instalaciones;
    const s = search.toLowerCase();
    return instalaciones.filter(i =>
      (i.clienteNombre || '').toLowerCase().includes(s) ||
      (i.numero || '').toLowerCase().includes(s) ||
      (i.tipo || '').toLowerCase().includes(s) ||
      (i.tecnicoAsignado || '').toLowerCase().includes(s)
    );
  }, [instalaciones, search]);

  // Agrupar para kanban por estado
  const byEstado = useMemo(() => {
    const groups = {};
    ESTADOS_INST.forEach(e => { groups[e.id] = []; });
    filtered.forEach(i => {
      const key = i.estado || 'en_curso';
      if (groups[key]) groups[key].push(i);
    });
    return groups;
  }, [filtered]);

  const handleSave = async (form) => {
    setSaving(true);
    setError('');
    try {
      const resp = await fetch('/.netlify/functions/saveInstalacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(form),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error al guardar');
      cargar();
      setShowForm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = (updated) => {
    setInstalaciones(prev => prev.map(i => i.id === updated.id ? updated : i));
    setDetalle(updated);
  };

  const stats = useMemo(() => ({
    total: instalaciones.length,
    en_curso: instalaciones.filter(i => i.estado === 'en_curso').length,
    completadas: instalaciones.filter(i => i.estado === 'completada').length,
    progMediano: instalaciones.length
      ? Math.round(instalaciones.reduce((s, i) => s + fasesProgress(i.fases || {}).pct, 0) / instalaciones.length)
      : 0,
  }), [instalaciones]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Instalaciones</h2>
            <p className="text-gray-500 text-sm mt-1">Seguimiento de proyectos de instalación por fases</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {[{ id: 'kanban', label: '⬛ Kanban' }, { id: 'lista', label: '☰ Lista' }].map(m => (
                <button
                  key={m.id}
                  onClick={() => setViewMode(m.id)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    viewMode === m.id ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setShowForm(true); setError(''); }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
            >
              <span className="text-lg leading-none">+</span> Nueva instalación
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          {[
            { label: 'Total', val: stats.total },
            { label: 'En curso', val: stats.en_curso, color: 'text-blue-600' },
            { label: 'Completadas', val: stats.completadas, color: 'text-green-600' },
            { label: 'Progreso medio', val: `${stats.progMediano}%`, color: 'text-red-600' },
          ].map(s => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
              <div className={`text-2xl font-bold ${s.color || 'text-gray-800'}`}>{s.val}</div>
              <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg border p-6">
          <h3 className="font-bold text-gray-800 mb-4">Nueva instalación</h3>
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-2 mb-4">{error}</div>}
          <InstalacionForm
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setError(''); }}
            saving={saving}
          />
        </div>
      )}

      {/* Búsqueda */}
      <div className="bg-white rounded-2xl shadow-lg border px-6 py-4">
        <input
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
          placeholder="Buscar por cliente, número, tipo o técnico…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : viewMode === 'kanban' ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {ESTADOS_INST.map(estado => (
            <div key={estado.id}>
              <div className={`text-xs font-bold uppercase px-3 py-1.5 rounded-lg mb-3 ${estado.color}`}>
                {estado.label} ({byEstado[estado.id]?.length || 0})
              </div>
              <div className="space-y-3">
                {byEstado[estado.id]?.length === 0 && (
                  <p className="text-center text-gray-300 text-xs py-4">Sin instalaciones</p>
                )}
                {byEstado[estado.id]?.map(inst => (
                  <InstCard key={inst.id} inst={inst} onClick={() => setDetalle(inst)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border p-6">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              {instalaciones.length === 0 ? 'No hay instalaciones. Crea la primera.' : 'Sin resultados.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {['Número', 'Cliente', 'Tipo', 'Técnico', 'Inicio', 'Progreso', 'Estado'].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(inst => {
                  const { done, total, pct } = fasesProgress(inst.fases || {});
                  const ei = estadoInfo(inst.estado);
                  return (
                    <tr key={inst.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setDetalle(inst)}>
                      <td className="py-3 px-3 font-mono text-xs text-gray-500">{inst.numero}</td>
                      <td className="py-3 px-3 font-semibold text-gray-800">{inst.clienteNombre}</td>
                      <td className="py-3 px-3 text-gray-600">{inst.tipo || '—'}</td>
                      <td className="py-3 px-3 text-gray-600">{inst.tecnicoAsignado || '—'}</td>
                      <td className="py-3 px-3 text-gray-500">{fmtDate(inst.fechaInicio)}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 w-8">{done}/{total}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ei.color}`}>{ei.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal detalle */}
      {detalle && (
        <InstalacionDetalle
          inst={detalle}
          onClose={() => setDetalle(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}
